import {
  createDraftAttachmentFromFile,
  createOutgoingDashboardMessage,
  filterDashboardContacts,
  filterDashboardThreads,
  getDashboardThreadById,
  getInitialDashboardThreadId,
  getMessageDayLabel,
  getSeededDashboardContacts,
  getSeededDashboardThreads,
  openOrCreateThreadForContact,
  upsertDashboardThread,
  appendMessageToThread,
} from "../../services/dashboardMessagesService";
import { getDefaultDashboardCurrentUser } from "../../messages.mock";
import {
  createAdminApiFailure,
  createAdminApiSuccess,
  normalizeAdminApiError,
} from "../../../shared/api/adminApiErrors";
import { buildApiUrl } from "../../../../services/apiUrl";
import { authenticatedFetch } from "../../../../lib/apiClient";
import type { AdminApiResult } from "../../../shared/api/types/adminApi";
import type {
  DashboardMessage,
  DashboardAttachment,
  DashboardThread,
} from "../../types.js";
import type {
  DashboardDraftAttachmentCreateRequestDto,
  DashboardKickoffThreadDraftRequestDto,
  DashboardMessageSendRequestDto,
  DashboardMessagesSeedDto,
  DashboardProjectKickoffThreadCreateRequestDto,
  DashboardProjectKickoffThreadCreateResultDto,
  DashboardThreadOpenOrCreateRequestDto,
  DashboardThreadOpenOrCreateResultDto,
  DashboardThreadMessageSendRequestDto,
  DashboardThreadMessageSendResultDto,
} from "../types/messages.api";
import {
  cloneDashboardThread,
  cloneDashboardThreads,
  mapDashboardMessageContactRecordToViewModel,
  mapDashboardMessageContactToRecord,
  mapDashboardThreadRecordToViewModel,
  mapDashboardThreadToRecord,
} from "../mappers/messagesMappers";
import { uploadAdminFile } from "../../../../services/viewerProjectService";

export function getSeededDashboardMessages(): DashboardMessagesSeedDto {
  return {
    threads: getSeededDashboardThreads().map(mapDashboardThreadToRecord),
    contacts: getSeededDashboardContacts().map(mapDashboardMessageContactToRecord),
  };
}

export async function fetchDashboardMessagesSeed(): Promise<
  AdminApiResult<DashboardMessagesSeedDto>
> {
  try {
    console.info("MESSAGES_BOOTSTRAP_REQUEST", {
      route: "/api/admin/messages/bootstrap",
    });
    const response = await authenticatedFetch(buildApiUrl("/api/admin/messages/bootstrap"), {
      method: "GET",
    });
    const payload = (await response.json()) as
      | { success: true; data: DashboardMessagesSeedDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      console.error("MESSAGES_BOOTSTRAP_ERROR", {
        status: response.status,
        code: payload.success ? "MESSAGES_BOOTSTRAP_FAILED" : payload.error?.code,
        message: payload.success
          ? "Unable to load workspace messages."
          : payload.error?.message ?? "Unable to load workspace messages.",
      });
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "MESSAGES_BOOTSTRAP_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to load workspace messages."
            : payload.error?.message ?? "Unable to load workspace messages.",
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("MESSAGES_BOOTSTRAP_THROW", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "MESSAGES_BOOTSTRAP_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to load workspace messages.",
      }),
    );
  }
}

export function cloneThreadsForState(
  threads: ReadonlyArray<DashboardThread>,
): DashboardThread[] {
  return cloneDashboardThreads(threads);
}

export function createMessageDraftAttachment(
  request: DashboardDraftAttachmentCreateRequestDto,
) {
  return createDraftAttachmentFromFile(request.file);
}

export function createProjectKickoffThreadDraft(
  request: DashboardKickoffThreadDraftRequestDto,
): DashboardThread {
  const createdAt = new Date();
  const currentUser = getDefaultDashboardCurrentUser();
  const sentAtLabel = createdAt.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const body =
    request.openingMessage.trim() ||
    (request.selectedFileCount > 0
      ? `Project intake created with ${request.selectedFileCount} staged file${request.selectedFileCount === 1 ? "" : "s"}.`
      : "Project intake created and ready for review.");

  return {
    id: `thread-${request.projectTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${createdAt.getTime().toString(36)}`,
    title: request.projectTitle,
    contextLabel: "Kickoff Thread",
    snippet: body,
    updatedAtLabel: sentAtLabel,
    updatedAtIso: createdAt.toISOString(),
    participantsLabel: "Me, client stakeholders",
    participants: [
      currentUser,
      {
        id: `stakeholder-${createdAt.getTime().toString(36)}`,
        name: request.companyName,
        avatarLabel: request.companyName
          .split(/\s+/)
          .slice(0, 2)
          .map((segment) => segment[0]?.toUpperCase() ?? "")
          .join("")
          .slice(0, 2),
        accent: "slate",
        presence: "offline",
        roleLabel: "Client stakeholder",
      },
    ],
    messages: [
      {
        id: `message-${createdAt.getTime().toString(36)}`,
        senderId: currentUser.id,
        sender: currentUser.name,
        sentAtLabel,
        sentAtIso: createdAt.toISOString(),
        body,
        direction: "outbound",
        status: "delivered",
      },
    ],
  };
}

export async function createProjectKickoffThreadRequest(
  request: DashboardProjectKickoffThreadCreateRequestDto,
): Promise<AdminApiResult<DashboardProjectKickoffThreadCreateResultDto>> {
  try {
    const response = await authenticatedFetch(
      buildApiUrl("/api/admin/messages/threads/project-kickoff"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId: request.projectId,
          openingMessage: request.openingMessage,
          selectedFileCount: request.selectedFileCount,
          uploadedFileCount: request.uploadedFileCount,
          failedUploadCount: request.failedUploadCount,
        }),
      },
    );
    const payload = (await response.json()) as
      | { success: true; data: DashboardProjectKickoffThreadCreateResultDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "MESSAGES_PROJECT_KICKOFF_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to create the kickoff thread right now."
            : payload.error?.message ?? "Unable to create the kickoff thread right now.",
          retryable: true,
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "MESSAGES_PROJECT_KICKOFF_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to create the kickoff thread right now.",
        retryable: true,
      }),
    );
  }
}

export function createOutgoingThreadMessage(
  request: DashboardMessageSendRequestDto,
) {
  return createOutgoingDashboardMessage(request);
}

function formatSentAtLabel(sentAtIso: string, fallbackLabel: string): string {
  const parsedDate = new Date(sentAtIso);
  if (!Number.isFinite(parsedDate.getTime())) {
    return fallbackLabel;
  }

  return parsedDate.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function patchThreadMessage(
  message: DashboardMessage,
  params: {
    sendResult: AdminApiResult<DashboardThreadMessageSendResultDto> | null;
    threadId: string;
  },
): DashboardMessage {
  if (!params.sendResult?.ok) {
    return {
      ...message,
      status: "failed",
    };
  }

  if (params.sendResult.data.threadId !== params.threadId) {
    return {
      ...message,
      status: "failed",
    };
  }

  return {
    ...message,
    id: params.sendResult.data.id,
    sentAtIso: params.sendResult.data.sentAtIso,
    sentAtLabel: formatSentAtLabel(
      params.sendResult.data.sentAtIso,
      message.sentAtLabel,
    ),
    status: params.sendResult.data.status,
  };
}

export function reconcileDashboardThreadMessageSend(
  threads: ReadonlyArray<DashboardThread>,
  params: {
    optimisticMessageId: string;
    sendResult: AdminApiResult<DashboardThreadMessageSendResultDto> | null;
    threadId: string;
  },
): DashboardThread[] {
  return threads.map((thread) => {
    if (thread.id !== params.threadId) {
      return thread;
    }

    const nextMessages = thread.messages.map((message) => {
      if (message.id !== params.optimisticMessageId) {
        return message;
      }

      return patchThreadMessage(message, {
        sendResult: params.sendResult,
        threadId: params.threadId,
      });
    });
    const updatedMessage = nextMessages.find(
      (message) => message.id === params.optimisticMessageId,
    );

    if (!params.sendResult?.ok || params.sendResult.data.threadId !== params.threadId) {
      return {
        ...thread,
        messages: nextMessages,
      };
    }

    return {
      ...thread,
      messages: nextMessages,
      updatedAtIso: params.sendResult.data.sentAtIso,
      updatedAtLabel: formatSentAtLabel(
        params.sendResult.data.sentAtIso,
        updatedMessage?.sentAtLabel ?? thread.updatedAtLabel,
      ),
    };
  });
}

export async function sendDashboardThreadMessage(
  request: DashboardThreadMessageSendRequestDto,
): Promise<AdminApiResult<DashboardThreadMessageSendResultDto>> {
  try {
    const invalidAttachment = request.attachments.find(
      (attachment) =>
        attachment.kind !== "image" &&
        attachment.kind !== "pdf" &&
        attachment.kind !== "video",
    );

    if (invalidAttachment) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: "INVALID_ATTACHMENT_KIND",
          message: "Message attachments must be image, PDF, or video files.",
        }),
      );
    }

    const attachmentRefs = await Promise.all(
      request.attachments.map(async (attachment) => {
        console.info("MESSAGE_ATTACHMENT_UPLOAD_INPUT", {
          threadId: request.threadId,
          fileName: attachment.file.name,
          kind: attachment.kind,
          size: attachment.file.size,
          projectId: null,
          scope: "workspace-authenticated",
        });
        const uploadedFile = await uploadAdminFile({
          file: attachment.file,
        });

        return {
          fileId: uploadedFile.fileId,
          kind: attachment.kind,
        };
      }),
    );

    const response = await authenticatedFetch(
      buildApiUrl(`/api/admin/messages/threads/${request.threadId}/messages`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clientMessageId: request.messageId,
          body: request.body,
          attachmentRefs,
        }),
      },
    );
    const payload = (await response.json()) as
      | {
          success: true;
          data: DashboardThreadMessageSendResultDto;
        }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      console.error("MESSAGE_SEND_ERROR", {
        threadId: request.threadId,
        status: response.status,
        code: payload.success ? "MESSAGES_SEND_FAILED" : payload.error?.code,
        message: payload.success
          ? "Unable to send this message right now."
          : payload.error?.message ?? "Unable to send this message right now.",
      });
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "MESSAGES_SEND_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to send this message right now."
            : payload.error?.message ?? "Unable to send this message right now.",
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("MESSAGE_SEND_THROW", {
      threadId: request.threadId,
      error,
    });
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "MESSAGES_SEND_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to send this message right now.",
      }),
    );
  }
}

export async function openOrCreateDashboardThreadForContactRequest(
  request: DashboardThreadOpenOrCreateRequestDto,
): Promise<AdminApiResult<DashboardThreadOpenOrCreateResultDto>> {
  try {
    const response = await authenticatedFetch(
      buildApiUrl("/api/admin/messages/threads/open-or-create"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contactAppUserId: request.contact.id,
        }),
      },
    );
    const payload = (await response.json()) as
      | { success: true; data: DashboardThreadOpenOrCreateResultDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "MESSAGES_THREAD_OPEN_OR_CREATE_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to open this conversation right now."
            : payload.error?.message ?? "Unable to open this conversation right now.",
          retryable: true,
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "MESSAGES_THREAD_OPEN_OR_CREATE_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to open this conversation right now.",
        retryable: true,
      }),
    );
  }
}

export {
  appendMessageToThread,
  filterDashboardContacts,
  filterDashboardThreads,
  getDashboardThreadById,
  getInitialDashboardThreadId,
  getMessageDayLabel,
  openOrCreateThreadForContact,
  upsertDashboardThread,
};
